import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { contractorsApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { DataTable } from '@/components/DataTable';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Star, User } from 'lucide-react';
import { format } from 'date-fns';
import DuplicateWarningPanel from '@/components/DuplicateWarningPanel';

const specialtyOptions = ['Roofing', 'Plumbing', 'Electrical', 'Structural', 'Flooring', 'Windows & Doors', 'HVAC', 'General Repairs'];
const regionOptions = ['Essex', 'Suffolk', 'Kent', 'Norfolk', 'London', 'National'];

export default function ContractorsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [contractors, setContractors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingContractor, setEditingContractor] = useState(null);
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [overrideDuplicateWarning, setOverrideDuplicateWarning] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    email: '',
    phone: '',
    specialties: [],
    regions: [],
    rating: '',
    notes: '',
    is_approved: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  // Handle URL parameter for auto-selecting contractor
  useEffect(() => {
    const contractorId = searchParams.get('id');
    if (contractorId && contractors.length > 0) {
      const contractor = contractors.find(c => c.id === contractorId);
      if (contractor) {
        setSelectedContractor(contractor);
        setShowProfile(true);
        // Clear the search param
        setSearchParams({});
      }
    }
  }, [searchParams, contractors, setSearchParams]);

  const fetchData = async () => {
    try {
      const data = await contractorsApi.getAll();
      setContractors(data);
    } catch (error) {
      toast.error('Failed to load contractors');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', company_name: '', email: '', phone: '', specialties: [], regions: [], rating: '', notes: '', is_approved: true });
    setEditingContractor(null);
    setOverrideDuplicateWarning(false);
  };

  const openDialog = (contractor = null) => {
    if (contractor) {
      setEditingContractor(contractor);
      setFormData({
        name: contractor.name || '',
        company_name: contractor.company_name || contractor.company || '',
        email: contractor.email || '',
        phone: contractor.phone || '',
        specialties: contractor.specialties || contractor.trades || [],
        regions: contractor.regions || [],
        rating: contractor.rating?.toString() || '',
        notes: contractor.notes || '',
        is_approved: contractor.is_approved !== false,
      });
    } else {
      resetForm();
    }
    setOverrideDuplicateWarning(false);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      rating: formData.rating ? parseFloat(formData.rating) : null,
      override_duplicate_warning: overrideDuplicateWarning,
    };
    try {
      if (editingContractor) {
        const result = await contractorsApi.update(editingContractor.id, submitData);
        // Check if it's a duplicate warning response
        if (result?.status === 'duplicate_warning') {
          toast.warning('Possible duplicate found. Review and click "Create Anyway" to proceed.');
          return;
        }
        toast.success('Contractor updated');
      } else {
        const result = await contractorsApi.create(submitData);
        // Check if it's a duplicate warning response
        if (result?.status === 'duplicate_warning') {
          toast.warning('Possible duplicate found. Review and click "Create Anyway" to proceed.');
          return;
        }
        toast.success('Contractor added');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      if (error.response?.data?.detail?.error === 'duplicate_blocked') {
        toast.error('Cannot create: Exact duplicate contractor exists');
      } else {
        toast.error('Failed to save contractor');
      }
    }
  };

  const handleUseExisting = (duplicate) => {
    // Navigate to the existing contractor
    setIsDialogOpen(false);
    const existingContractor = contractors.find(c => c.id === duplicate.id);
    if (existingContractor) {
      setSelectedContractor(existingContractor);
      setShowProfile(true);
    }
    toast.info(`Using existing contractor: ${duplicate.company_name || duplicate.email}`);
  };

  const handleDelete = async (contractor) => {
    if (!window.confirm('Delete this contractor?')) return;
    try {
      await contractorsApi.delete(contractor.id);
      toast.success('Contractor deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete contractor');
    }
  };

  const toggleSpecialty = (specialty) => {
    const current = formData.specialties || [];
    if (current.includes(specialty)) {
      setFormData({ ...formData, specialties: current.filter(s => s !== specialty) });
    } else {
      setFormData({ ...formData, specialties: [...current, specialty] });
    }
  };

  const toggleRegion = (region) => {
    const current = formData.regions || [];
    if (current.includes(region)) {
      setFormData({ ...formData, regions: current.filter(r => r !== region) });
    } else {
      setFormData({ ...formData, regions: [...current, region] });
    }
  };

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Contact', render: (val, row) => {
      const email = val || row?.primary_contact?.email;
      const phone = row?.phone;
      return (
        <div className="text-sm">
          {email && <div className="text-blue-600">{email}</div>}
          {phone && <div className="text-slate-500">{phone}</div>}
          {!email && !phone && <span className="text-slate-400">-</span>}
        </div>
      );
    }},
    { key: 'trades', label: 'Trades', render: (val, row) => {
      const items = val || row?.specialties || [];
      return (
        <div className="flex flex-wrap gap-1">
          {items?.slice(0, 2).map((s, i) => (
            <Badge key={i} variant="secondary" className="text-xs bg-stone-100">{s}</Badge>
          ))}
          {items?.length > 2 && <Badge variant="secondary" className="text-xs bg-stone-100">+{items.length - 2}</Badge>}
        </div>
      );
    }},
    { key: 'region', label: 'Region', render: (val, row) => val || row?.regions?.join(', ') || '-' },
    { key: 'framework_supplier', label: 'Framework', render: (val) => val ? (
      <Badge className="bg-blue-100 text-blue-800">Framework</Badge>
    ) : null },
    { key: 'auto_assign_status', label: 'Status', render: (val) => val === 'eligible' ? (
      <Badge className="bg-green-100 text-green-800">Eligible</Badge>
    ) : val ? (
      <Badge className="bg-amber-100 text-amber-800">{val}</Badge>
    ) : '-' },
  ];

  const handleViewProfile = (contractor) => {
    setSelectedContractor(contractor);
    setShowProfile(true);
  };

  const actions = [
    { label: 'View Profile', icon: User, onClick: handleViewProfile },
    { label: 'Edit', icon: Pencil, onClick: openDialog },
    { label: 'Delete', icon: Trash2, onClick: handleDelete, destructive: true },
  ];

  return (
    <div className="space-y-6" data-testid="contractors-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Contractors</h1>
          <p className="text-sm text-stone-500 mt-1">Manage your contractor network</p>
        </div>
        <Button onClick={() => openDialog()} className="bg-emerald-900 hover:bg-emerald-800" data-testid="add-contractor-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Contractor
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={contractors}
        actions={actions}
        isLoading={isLoading}
        emptyMessage="No contractors found. Add your first contractor!"
      />

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="contractor-dialog">
          <DialogHeader>
            <DialogTitle>{editingContractor ? 'Edit Contractor' : 'Add New Contractor'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="contractor-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company_name">Company</Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  data-testid="contractor-company-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="contractor-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="contractor-phone-input"
                />
              </div>
            </div>
            
            {/* Duplicate Warning Panel */}
            <DuplicateWarningPanel
              entityType="contractor"
              checkData={{
                company_name: formData.company_name,
                email: formData.email,
                phone: formData.phone
              }}
              excludeId={editingContractor?.id}
              onUseExisting={handleUseExisting}
              onOverride={() => setOverrideDuplicateWarning(true)}
            />
            
            <div className="space-y-2">
              <Label>Specialties</Label>
              <div className="flex flex-wrap gap-2">
                {specialtyOptions.map((s) => (
                  <Badge
                    key={s}
                    variant="outline"
                    className={`cursor-pointer ${formData.specialties?.includes(s) ? 'bg-emerald-100 border-emerald-500' : ''}`}
                    onClick={() => toggleSpecialty(s)}
                  >
                    {s}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Regions</Label>
              <div className="flex flex-wrap gap-2">
                {regionOptions.map((r) => (
                  <Badge
                    key={r}
                    variant="outline"
                    className={`cursor-pointer ${formData.regions?.includes(r) ? 'bg-sky-100 border-sky-500' : ''}`}
                    onClick={() => toggleRegion(r)}
                  >
                    {r}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="rating">Rating (1-5)</Label>
                <Input
                  id="rating"
                  type="number"
                  min="1"
                  max="5"
                  step="0.1"
                  value={formData.rating}
                  onChange={(e) => setFormData({ ...formData, rating: e.target.value })}
                  data-testid="contractor-rating-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="is_approved">Approved</Label>
                <div className="flex items-center space-x-2 pt-2">
                  <Switch
                    id="is_approved"
                    checked={formData.is_approved}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_approved: checked })}
                    data-testid="contractor-approved-switch"
                  />
                  <Label htmlFor="is_approved" className="font-normal">
                    {formData.is_approved ? 'Approved' : 'Not Approved'}
                  </Label>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                data-testid="contractor-notes-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-900 hover:bg-emerald-800" data-testid="contractor-submit-btn">
                {editingContractor ? 'Update' : 'Add'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Contractor Profile Dialog */}
      <Dialog open={showProfile} onOpenChange={setShowProfile}>
        <DialogContent className="max-w-2xl" data-testid="contractor-profile-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <User className="h-6 w-6 text-emerald-700" />
              </div>
              <div>
                <div className="text-xl">{selectedContractor?.company_name || selectedContractor?.name}</div>
                <div className="text-sm font-normal text-slate-500">{selectedContractor?.name}</div>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {selectedContractor && (
            <div className="space-y-6 mt-4">
              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500 mb-1">Email</p>
                  <p className="font-medium">{selectedContractor.email || selectedContractor.primary_contact?.email || '-'}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500 mb-1">Phone</p>
                  <p className="font-medium">{selectedContractor.phone || '-'}</p>
                </div>
              </div>

              {/* Rating */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500 mb-2">Rating</p>
                <div className="flex items-center gap-1">
                  {[1,2,3,4,5].map((star) => (
                    <Star 
                      key={star} 
                      className={`h-5 w-5 ${star <= (selectedContractor.rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`}
                    />
                  ))}
                  <span className="ml-2 font-medium">{selectedContractor.rating || 'Not rated'}/5</span>
                </div>
              </div>

              {/* Specialties & Regions */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500 mb-2">Specialties</p>
                  <div className="flex flex-wrap gap-2">
                    {(selectedContractor.specialties || []).length > 0 ? (
                      selectedContractor.specialties.map((s, idx) => (
                        <Badge key={idx} className="bg-emerald-100 text-emerald-700">{s}</Badge>
                      ))
                    ) : (
                      <span className="text-slate-400">None specified</span>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-2">Service Regions</p>
                  <div className="flex flex-wrap gap-2">
                    {(selectedContractor.regions || selectedContractor.coverage_areas || []).length > 0 ? (
                      (selectedContractor.regions || selectedContractor.coverage_areas).map((r, idx) => (
                        <Badge key={idx} variant="outline">{r}</Badge>
                      ))
                    ) : (
                      <span className="text-slate-400">None specified</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  <p className="font-medium">{selectedContractor.is_approved ? 'Approved' : 'Pending Approval'}</p>
                </div>
                <Badge className={selectedContractor.is_approved ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                  {selectedContractor.is_approved ? 'Active' : 'Inactive'}
                </Badge>
              </div>

              {/* Notes */}
              {selectedContractor.notes && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500 mb-1">Notes</p>
                  <p className="text-sm">{selectedContractor.notes}</p>
                </div>
              )}

              {/* Actions */}
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setShowProfile(false)}>
                  Close
                </Button>
                <Button 
                  className="bg-emerald-900 hover:bg-emerald-800"
                  onClick={() => {
                    setShowProfile(false);
                    openDialog(selectedContractor);
                  }}
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit Contractor
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
